import BankApplicationBusinessMembers from '@/components/bankAccountApplications/details/BankApplicationBusinessMembers';
import BankApplicationDetailsButtons from '@/components/bankAccountApplications/details/BankApplicationDetailsButtons';
import BankApplicationDocuments from '@/components/bankAccountApplications/details/BankApplicationDocuments';
import BankApplicationKYCInfo from '@/components/bankAccountApplications/details/BankApplicationKYCInfo';
import BankAccountApplicationPendingActions from '@/components/bankAccountApplications/details/BankApplicationPendingActions';
import BankApplicationRequirementsCard from '@/components/bankAccountApplications/details/BankApplicationRequirementsCard';
import BankApplicationSignatures from '@/components/bankAccountApplications/details/BankApplicationSignatures';
import BankApplicationStaffNotes from '@/components/bankAccountApplications/details/BankApplicationStaffNotes';
import BankApplicationSubmissionDetails from '@/components/bankAccountApplications/details/BankApplicationSubmissionDetails';
import { SVLoader } from '@/components/core';
import DefaultLayout from '@/components/layouts/DefaultLayout';
import { ROUTES } from '@/constants/routes';
import { useGetBankApplicationDetails } from '@/hooks/requests/useBankApplications';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import {
  BankApplicationAccountType,
  BankApplicationStatus,
} from '@/types/bankApplications';
import {
  getBankAccountApplicationTitle,
  getBankApplicationAccountStatusLabel,
} from '@/utils/bankAccountApplications';
import { Card, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';

export default function BankAccountApplicationDetailsPage() {
  const { isStaff } = useAuthUser();
  const { uuid = '' } = useParams();
  const { bankApplication, isBankApplicationLoading } =
    useGetBankApplicationDetails(uuid);

  if (isBankApplicationLoading) {
    return (
      <DefaultLayout header="Bank Application Details">
        <SVLoader />
      </DefaultLayout>
    );
  }

  if (!bankApplication) {
    return (
      <DefaultLayout header="Bank Application Details">
        <Card>
          <Typography>
            Something went wrong retreiving the bank account application. Our
            team has been notified.
          </Typography>
        </Card>
      </DefaultLayout>
    );
  }

  const {
    primaryOwner,
    business,
    status,
    documents,
    requirements,
    kycEvaluations,
    accountType,
  } = bankApplication;
  const header = getBankAccountApplicationTitle({
    fullName: primaryOwner ? primaryOwner.consumer.fullName : '',
    email: primaryOwner ? primaryOwner.consumer.email : '',
    businessName: business ? business.name : '',
  });

  const isBusinessAccount = accountType === BankApplicationAccountType.BUSINESS;

  const isNeedsReview = status === BankApplicationStatus.NEED_REVIEW;
  const showBankApplicationStaffNotes =
    isStaff &&
    (status === BankApplicationStatus.NEED_REVIEW ||
      status === BankApplicationStatus.AWAITING_E_SIGN ||
      status === BankApplicationStatus.SUBMITTED ||
      status === BankApplicationStatus.COMPLETED);
  const showRequirements =
    status !== BankApplicationStatus.COMPLETED &&
    status !== BankApplicationStatus.DECLINED;

  return (
    <DefaultLayout
      header={header}
      breadcrumb={[
        {
          label: `Bank Account Application - ${getBankApplicationAccountStatusLabel(status)}`,
          href: ROUTES.BANK_ACCOUNT_APPLICATIONS_DRAFT,
        },
        { label: header, isActive: true },
      ]}
      buttons={[
        <BankApplicationDetailsButtons
          key="bankApplicationButtons"
          bankApplication={bankApplication}
        />,
      ]}>
      <Stack spacing={2}>
        {status === BankApplicationStatus.AWAITING_E_SIGN && (
          <BankApplicationSignatures bankApplication={bankApplication} />
        )}
        <BankApplicationSubmissionDetails bankApplication={bankApplication} />
        {isBusinessAccount && (
          <BankApplicationBusinessMembers businessId={business?.uuid} />
        )}
        <BankApplicationDocuments documents={documents} uuid={uuid} />
        <BankApplicationKYCInfo
          isBusinessAccount={isBusinessAccount}
          kycEvaluations={kycEvaluations}
        />
        {showRequirements && (
          <BankApplicationRequirementsCard requirements={requirements} />
        )}
        {showBankApplicationStaffNotes && (
          <BankApplicationStaffNotes
            thirdPartyProcessorStatus={
              bankApplication.thirdPartyProcessorStatus
            }
          />
        )}
        {isNeedsReview && (
          <BankAccountApplicationPendingActions
            bankApplication={bankApplication}
          />
        )}
      </Stack>
    </DefaultLayout>
  );
}
